import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import client from "@culturehq/client";
import { Icon, Loader, Pagination } from "@culturehq/components";

import { parseQueryString, Trans } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import GalleryPhoto from "components/common/gallery/Photo";
import EmptyGallery from "components/common/gallery/EmptyGallery";
import LightboxModal from "components/common/gallery/LightboxModal";

class EventPhotos extends Component {
  state = {
    prevPage: {},
    currentPage: {},
    nextPage: {},
    loading: true,
    modalIsOpen: false,
    lightboxedPhoto: null
  };

  componentDidMount() {
    this.fetchPhotos();
  }

  fetchPhotos = async selectedPage => {
    const { location: { search } } = this.props;

    let page = selectedPage;

    // If no page is provided, check
    // the query params for ?page=
    if (!page) {
      const parsed = parseQueryString(search);
      page = parsed.page ? Number(parsed.page) : 1;
    }

    const current = await this.loadData(page);

    this.setState({
      currentPage: current,
      loading: false
    });

    const { totalPages } = current.pagination;

    // Wrapping
    const prev = page - 1 < 1 ? totalPages : page - 1;
    const next = page + 1 > totalPages ? 1 : page + 1;

    // Store previous and next pages
    // for quick photo looping.
    const prevPage = await this.loadData(prev);
    const nextPage = await this.loadData(next);

    this.setState({
      prevPage,
      nextPage
    });
  };

  loadData = async page => {
    const { event } = this.props;
    const options = { page: Number(page) };

    if (event) {
      options.eventId = event.id;
    }

    const { photos, pagination } = await client.listEventPhotos(options);

    if (event) {
      // Note: Ensure that photos have their corresponding
      // event populated on the object. The gallery API call
      // will do this automatically... however the individual
      // events photos call needs the data added.
      photos.forEach(photo => Object.assign(photo, { event }));
    }

    return { photos, pagination };
  };

  loadNextPage = async page => {
    const { currentPage: prevPage, nextPage: currentPage } = this.state;

    this.setState({
      lightboxedPhoto: currentPage.photos[0],
      currentPage,
      prevPage
    });

    const { totalPages } = currentPage.pagination;
    const next = page + 1 > totalPages ? 1 : page + 1;
    const nextPage = await this.loadData(next);

    this.setState({ nextPage });
  };

  loadPrevPage = async page => {
    const { prevPage: currentPage, currentPage: nextPage } = this.state;

    this.setState({
      lightboxedPhoto: currentPage.photos[currentPage.photos.length - 1],
      currentPage,
      nextPage
    });

    const { totalPages } = currentPage.pagination;
    const prev = page - 1 < 1 ? totalPages : page - 1;
    const prevPage = await this.loadData(prev);

    this.setState({ prevPage });
  };

  // User has selected a new page of results
  handlePageSelected = page => {
    const { history } = this.props;

    history.push(`?page=${page}`);
    this.fetchPhotos(page);
  };

  // Attempt to change the focused photo
  // target: intended local index.
  handlePhotoChange = target => {
    const { currentPage: { pagination, photos } } = this.state;

    // Next Page
    if (target + 1 === 0) {
      this.loadPrevPage(pagination.currentPage - 1);
      return;
    }

    // Prev Page
    if (target + 1 > photos.length) {
      this.loadNextPage(pagination.currentPage + 1);
      return;
    }

    this.setState({
      lightboxedPhoto: photos[target],
      modalIsOpen: true
    });
  };

  // User has deleted a photo.
  // Remove photo from memory
  handlePhotoDelete = deleted => {
    const { currentPage } = this.state;

    this.setState({
      currentPage: {
        ...currentPage,
        photos: currentPage.photos.filter(({ id }) => id !== deleted.id)
      },
      modalIsOpen: false
    });
  };

  handleClose = () => {
    this.setState({ modalIsOpen: false });
  };

  // Note: When a user cheers a photo, we need to update that
  // photo in memory to reflect the cheerForUser/cheersCount
  // for when they return to that photo (via the carousel)
  handlePhotoCheerToggle = (photoId, cheerForUser, cheersCount) => {
    this.setState(({ currentPage }) => ({
      currentPage: {
        ...currentPage,

        // Iterate through all photos on the current page of results.
        photos: currentPage.photos.map(photo => {
          if (photo.id !== photoId) return photo;

          // Update the photo's data.
          return {
            ...photo,
            cheerForUser,
            cheersCount
          };
        })
      }
    }));

    const { onPhotoCheerToggle } = this.props;

    if (onPhotoCheerToggle) {
      onPhotoCheerToggle("photos", photoId, { cheerForUser, cheersCount });
    }
  };

  handlePhotoUpdate = update => {
    const { lightboxedPhoto } = this.state;
    this.setState(({ currentPage }) => ({
      currentPage: {
        ...currentPage,
        photos: currentPage.photos.map(photo => (
          photo.id === update.id ? { ...photo, ...update } : photo
        ))
      },
      lightboxedPhoto: lightboxedPhoto.id === update.id
        ? { ...lightboxedPhoto, ...update } : lightboxedPhoto
    }));
  };

  render() {
    const { event } = this.props;
    const { lightboxedPhoto, modalIsOpen, loading, currentPage } = this.state;

    const { pagination, photos = [] } = currentPage;

    return (
      <>
        <div className="Photos clearfix">
          <div className="col-xs-12">
            <Loader loading={loading}>
              <ErrorBoundary>
                {photos.map(photo => (
                  <GalleryPhoto
                    key={photo.id}
                    photos={photos}
                    photo={photo}
                    onPhotoChange={this.handlePhotoChange}
                    narrowLayout
                  />
                ))}

                {photos.length === 0 && <EmptyGallery photos={photos} />}

                <Pagination {...pagination} onClick={this.handlePageSelected} />
              </ErrorBoundary>
            </Loader>
          </div>
          <div className="col-xs-12">
            <Link className="button" to={`/events/${event.id}/photos/create`}>
              <Icon icon="plus" />
              {" "}
              <Trans>Add photos</Trans>
            </Link>
          </div>
        </div>
        <LightboxModal
          activePhoto={lightboxedPhoto}
          photos={photos}
          modalIsOpen={modalIsOpen}
          onClose={this.handleClose}
          onPhotoChange={this.handlePhotoChange}
          onDelete={this.handlePhotoDelete}
          onPhotoCheerToggle={this.handlePhotoCheerToggle}
          onPhotoUpdate={this.handlePhotoUpdate}
        />
      </>
    );
  }
}

export default withRouter(EventPhotos);
